import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import {
  Landmark,
  Wallet,
  Plus,
  Trash2,
  Edit2,
  Building2,
  MapPin,
  CreditCard,
  Hash,
} from 'lucide-react';
import {
  useBankAccounts,
  useCreateBankAccount,
  type NewBankAccount,
  type BankAccountRow,
} from '../lib/finance';
import { useDeleteBankAccount, useUpdateBankAccount } from '../lib/directory-edit';
import Shell from '../components/Shell';
import Confirm, { useConfirm } from '../components/Confirm';
import { useRestore } from '../lib/restore';
import { guardBankAccount } from '../lib/delete-guards';
import {
  Drawer,
  Field,
  EmptyState,
  TableSkeleton,
  ErrorState,
  useToast,
  Select,
} from '../components/ui';

export default function BankAccounts() {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<BankAccountRow | null>(null);
  const confirm = useConfirm<BankAccountRow>();
  const restore = useRestore();
  const remove = useDeleteBankAccount();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useBankAccounts();

  async function doDelete() {
    if (!confirm.target) return;
    try {
      await remove.mutateAsync(confirm.target.id);
      const removed = confirm.target;
      toast.showWithAction(removed.bankName + ' removed', {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'bank_accounts', id: removed.id }),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the account', true);
    } finally {
      confirm.close();
    }
  }

  // Account directory metrics (no financial amounts)
  const totalAccounts = data?.length ?? 0;
  const bankCount = data?.filter((a) => !a.isCash).length ?? 0;
  const cashCount = data?.filter((a) => a.isCash).length ?? 0;
  const branchCount = new Set(data?.map((a) => a.branch).filter(Boolean)).size;

  return (
    <Shell
      title="Company Bank Accounts & Cash"
      crumb="Finance"
      actions={
        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setAdding(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
        >
          <Plus size={15} strokeWidth={2.2} />
          <span>Add Account</span>
        </button>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <p style={{ marginTop: 0, marginBottom: 18, color: 'var(--text-soft)', fontSize: 13.5 }}>
          Company operational bank accounts and registered payment channels used for client receipts, vendor transfers, and payment vouchers.
        </p>

        {/* Directory KPIs (No financial amounts) */}
        {!isLoading && !!data?.length && (
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Accounts</span>
                <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Landmark size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{totalAccounts}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                {bankCount} Bank Accounts · {cashCount} Petty Cash
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Commercial Banks</span>
                <div style={{ color: '#2563eb', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Building2 size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#2563eb' }}>
                {bankCount}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Active bank accounts
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Operating Branches</span>
                <div style={{ color: '#059669', background: 'rgba(5, 150, 105, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <MapPin size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#059669' }}>
                {branchCount}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Unique branch locations
              </div>
            </div>
          </div>
        )}

        {/* Bank Accounts Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {isLoading ? (
              <TableSkeleton rows={3} cols={7} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : !data?.length ? (
              <EmptyState
                icon="bank"
                title="No accounts registered"
                hint="Add your company bank accounts and payment details."
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Bank Name</th>
                    <th>Branch</th>
                    <th>Account Type</th>
                    <th>Account Number</th>
                    <th>IFSC Code</th>
                    <th>Account Holder</th>
                    <th style={{ width: 80, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((a) => (
                    <tr key={a.id}>
                      {/* Bank Name */}
                      <td>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 12,
                            textAlign: 'left',
                          }}
                        >
                          <div
                            className="avatar-sq"
                            style={{
                              width: 36,
                              height: 36,
                              fontSize: 13,
                              fontWeight: 700,
                              borderRadius: 'var(--radius-sm)',
                              background: a.isCash
                                ? 'linear-gradient(135deg, rgba(234, 88, 12, 0.1), rgba(234, 88, 12, 0.02))'
                                : 'linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(37, 99, 235, 0.02))',
                              color: a.isCash ? '#ea580c' : 'var(--brand)',
                              border: `1px solid ${a.isCash ? 'rgba(234, 88, 12, 0.2)' : 'rgba(37, 99, 235, 0.2)'}`,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {a.isCash ? <Wallet size={16} /> : <Landmark size={16} />}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13.5 }}>
                              {a.bankName}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Branch Name */}
                      <td>
                        {a.branch ? (
                          <span style={{ color: 'var(--text)', fontSize: 13 }}>
                            {a.branch}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-faint)' }}>—</span>
                        )}
                      </td>

                      {/* Account Type */}
                      <td>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            padding: '2.5px 8px',
                            borderRadius: 'var(--radius-pill)',
                            background: a.isCash ? '#fff7ed' : '#eff6ff',
                            color: a.isCash ? '#c2410c' : '#1d4ed8',
                            border: `1px solid ${a.isCash ? '#ffedd5' : '#bfdbfe'}`,
                          }}
                        >
                          {a.isCash ? 'Petty Cash' : 'Bank Account'}
                        </span>
                      </td>

                      {/* Account Number */}
                      <td className="num">
                        {a.accountNo ? (
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-soft)' }}>
                            {a.accountNo}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-faint)' }}>—</span>
                        )}
                      </td>

                      {/* IFSC Code */}
                      <td className="num">
                        {a.ifsc ? (
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 12,
                              background: 'var(--surface-2)',
                              padding: '2px 7px',
                              borderRadius: 'var(--radius-xs)',
                              border: '1px solid var(--border)',
                              color: 'var(--text-soft)',
                            }}
                          >
                            {a.ifsc}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-faint)' }}>—</span>
                        )}
                      </td>

                      {/* Account Holder Name */}
                      <td>
                        <span style={{ fontWeight: 500, color: 'var(--text)' }}>
                          {a.accountHolder}
                        </span>
                      </td>

                      {/* Actions */}
                      <td>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                          <button
                            className="btn btn-auto btn-sm btn-ghost"
                            onClick={() => setEditing(a)}
                            title={'Edit ' + a.bankName}
                            style={{
                              color: 'var(--text-soft)',
                              padding: 6,
                              borderRadius: 'var(--radius-xs)',
                            }}
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            className="btn btn-auto btn-sm btn-ghost"
                            onClick={() => confirm.ask(a)}
                            title={'Delete ' + a.bankName}
                            style={{
                              color: 'var(--text-faint)',
                              padding: 6,
                              borderRadius: 'var(--radius-xs)',
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {adding && <AccountDrawer onClose={() => setAdding(false)} />}
      {editing && <AccountDrawer existing={editing} onClose={() => setEditing(null)} />}

      {confirm.target && (
        <Confirm
          title={'Delete ' + confirm.target.bankName + '?'}
          message="It will disappear from your account list. You can undo this straight away from the notification that appears."
          check={guardBankAccount(confirm.target.id)}
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        />
      )}
    </Shell>
  );
}

interface AccountFormFields {
  bank_name: string;
  account_holder: string;
  branch: string;
  account_no: string;
  ifsc: string;
  is_cash: boolean;
}

const EMPTY_FORM: AccountFormFields = {
  bank_name: '',
  account_holder: '',
  branch: '',
  account_no: '',
  ifsc: '',
  is_cash: false,
};

function AccountDrawer({
  existing,
  onClose,
}: {
  existing?: BankAccountRow;
  onClose: () => void;
}) {
  const [form, setForm] = useState<AccountFormFields>(() => {
    if (!existing) return EMPTY_FORM;
    return {
      bank_name: existing.bankName,
      account_holder: existing.accountHolder,
      branch: existing.branch ?? '',
      account_no: existing.accountNo ?? '',
      ifsc: existing.ifsc ?? '',
      is_cash: existing.isCash,
    };
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useCreateBankAccount();
  const update = useUpdateBankAccount();
  const remove = useDeleteBankAccount();
  const confirm = useConfirm<BankAccountRow>();
  const toast = useToast();

  const isEdit = !!existing;
  const busy = create.isPending || update.isPending || remove.isPending;

  const set = (k: keyof AccountFormFields) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.bank_name.trim()) next.bank_name = 'Account or bank name is required';
    if (!form.account_holder.trim()) next.account_holder = 'Account holder name is required';
    setErrors(next);
    if (Object.keys(next).length) return;

    try {
      if (isEdit) {
        await update.mutateAsync({
          id: existing.id,
          bank_name: form.bank_name,
          account_holder: form.account_holder,
          branch: form.branch,
          account_no: form.account_no,
          ifsc: form.ifsc,
          is_cash: form.is_cash,
        });
        toast.show(`${form.bank_name} updated successfully`);
      } else {
        await create.mutateAsync({
          bank_name: form.bank_name,
          account_holder: form.account_holder,
          branch: form.branch,
          account_no: form.account_no,
          ifsc: form.ifsc,
          is_cash: form.is_cash,
          openingRupees: '0',
        });
        toast.show(`${form.bank_name} registered successfully`);
      }
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the account', true);
    }
  }

  async function doDelete() {
    if (!existing) return;
    try {
      await remove.mutateAsync(existing.id);
      toast.show(`${existing.bankName} removed`);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not remove account', true);
    }
  }

  return (
    <>
      <Drawer
        title={isEdit ? 'Edit Bank Account' : 'Add Bank Account'}
        description={isEdit ? 'Update registered bank account details' : 'Register a company bank account or site cash register'}
        onClose={onClose}
        onSubmit={onSubmit}
        submitLabel={isEdit ? 'Save Changes' : 'Save Account'}
        busy={busy}
        onDelete={isEdit ? () => confirm.ask(existing) : undefined}
      >
        <div className="form-grid">
          <Field label="Account Type" span2>
            <Select
              value={form.is_cash ? 'cash' : 'bank'}
              onChange={(val) => setForm((f) => ({ ...f, is_cash: val === 'cash' }))}
              options={[
                { value: 'bank', label: 'Bank Account (Current / Savings / OD)' },
                { value: 'cash', label: 'Petty Cash / Site Cash Wallet' },
              ]}
            />
          </Field>

          <Field label={form.is_cash ? 'Cash Register Name' : 'Bank Name'} error={errors.bank_name} span2>
            <input
              value={form.bank_name}
              onChange={set('bank_name')}
              autoFocus
              aria-invalid={!!errors.bank_name}
            />
          </Field>

          <Field label="Account Holder Name" error={errors.account_holder} span2>
            <input
              value={form.account_holder}
              onChange={set('account_holder')}
              aria-invalid={!!errors.account_holder}
            />
          </Field>

          {!form.is_cash && (
            <>
              <Field label="Branch Name" optional>
                <input
                  value={form.branch}
                  onChange={set('branch')}
                />
              </Field>

              <Field label="Account Number" optional>
                <input
                  value={form.account_no}
                  onChange={set('account_no')}
                  inputMode="numeric"
                />
              </Field>

              <Field label="IFSC Code" optional span2>
                <input
                  value={form.ifsc}
                  onChange={set('ifsc')}
                  style={{ textTransform: 'uppercase' }}
                />
              </Field>
            </>
          )}
        </div>
      </Drawer>

      {confirm.target && (
        <Confirm
          title={'Delete ' + confirm.target.bankName + '?'}
          message="It will disappear from your account list."
          check={guardBankAccount(confirm.target.id)}
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        />
      )}
    </>
  );
}

